<script setup lang="ts">
import { computed, ref } from "vue";
import { UiCard, UiText, UiTooltip, UiIcon, UiSegmentedControl } from "@shopexperts/shared";
import type { AdminWalletStatisticsOverview } from "@shopexperts/legacy-api";
import { formatCurrency } from "@/v2Projects/utils/display";
import {
  ACTIVITY_FLOWS,
  ACTIVITY_WINDOW_OPTIONS,
  LIABILITY_COLORS,
  type ActivityWindowKey,
} from "./walletStatisticsDisplay";

const props = defineProps<{
  activity?: AdminWalletStatisticsOverview["activity"];
  currency?: string;
  isLoading: boolean;
}>();

const windowKey = ref<ActivityWindowKey>("this_month");

const window = computed(() => props.activity?.[windowKey.value]);

const maxFlowCents = computed(() => {
  if (!window.value) return 0;
  return Math.max(...ACTIVITY_FLOWS.map((flow) => Math.abs(window.value?.[flow.key] ?? 0)), 0);
});

const flowRows = computed(() =>
  ACTIVITY_FLOWS.map((flow) => {
    const cents = Math.abs(window.value?.[flow.key] ?? 0);
    return {
      ...flow,
      cents,
      pct: maxFlowCents.value > 0 ? (cents / maxFlowCents.value) * 100 : 0,
      colors: LIABILITY_COLORS[flow.polarity],
      sign: cents === 0 ? "" : flow.polarity === "added" ? "+" : "−",
    };
  }),
);

const delta = computed(() => window.value?.owed_delta_cents ?? 0);

const WINDOW_BASELINES: Record<ActivityWindowKey, string> = {
  this_month: "month to date",
  last_month: "last month",
  last_30_days: "last 30 days",
  all_time: "all time",
};

const deltaSign = computed(() => (delta.value > 0 ? "+" : delta.value < 0 ? "−" : ""));

const deltaLabel = computed(() => `net liability change · ${WINDOW_BASELINES[windowKey.value]}`);

const DELTA_HINT =
  "Liability is everything the company currently owes experts (available + on hold across their wallets). " +
  "This is its net movement over the selected period: earnings and admin adjustments add to it, payouts and refunds clear it. " +
  "+ means the company owed more at the end of the period than at the start; − means it paid down more than experts earned.";

const deltaClass = computed(() => {
  if (delta.value > 0) return LIABILITY_COLORS.added.text;
  if (delta.value < 0) return LIABILITY_COLORS.available.text;
  return "text-gray-500";
});
</script>

<template>
  <UiCard class="h-full">
    <div class="flex h-full flex-col gap-4 p-2">
      <div class="flex flex-col gap-3">
        <UiText as="h3" variant="h6" class="font-semibold" text="Liability flow" />
        <UiSegmentedControl
          v-model="windowKey"
          :options="ACTIVITY_WINDOW_OPTIONS"
          aria-label="Activity time window"
        />
      </div>

      <div v-if="isLoading || !window" class="flex flex-1 flex-col gap-3">
        <div v-for="n in 5" :key="n" class="h-6 animate-pulse rounded-md bg-se-gray-100" />
      </div>

      <template v-else>
        <!-- Net change headline -->
        <div class="flex items-baseline gap-2">
          <span class="text-2xl font-bold tabular-nums" :class="deltaClass">
            {{ deltaSign }}{{ formatCurrency(Math.abs(delta), currency) }}
          </span>
          <span class="flex items-center gap-1.5">
            <UiText as="span" variant="p-small" class="text-gray-500" :text="deltaLabel" />
            <UiTooltip :content="DELTA_HINT">
              <UiIcon name="help-circle" class="h-3.5 w-3.5 cursor-default text-gray-400" />
            </UiTooltip>
          </span>
        </div>

        <!-- Flow bars -->
        <div class="flex flex-col gap-2.5">
          <div v-for="flow in flowRows" :key="flow.key" class="grid grid-cols-[130px_1fr_100px] items-center gap-3">
            <UiTooltip :content="flow.hint">
              <span class="flex cursor-default items-center gap-1.5 text-xs text-gray-600">
                <span class="h-1.5 w-1.5 shrink-0 rounded-full" :class="flow.colors.bar" />
                <span class="truncate">{{ flow.label }}</span>
              </span>
            </UiTooltip>
            <div class="h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div
                class="h-full rounded-full transition-[width] duration-500 ease-out"
                :class="flow.colors.bar"
                :style="{ width: `${flow.cents > 0 ? Math.max(flow.pct, 1.5) : 0}%` }"
              />
            </div>
            <span class="text-right text-xs font-semibold tabular-nums" :class="flow.cents > 0 ? flow.colors.text : 'text-gray-300'">
              {{ flow.sign }}{{ formatCurrency(flow.cents, currency) }}
            </span>
          </div>
        </div>

        <!-- Hold releases don't change the total owed, so they sit apart from the flows -->
        <div class="mt-auto flex items-center justify-between gap-3 border-t border-se-gray-200 pt-3">
          <UiTooltip content="Moved from hold to available in this window — changes what's payable now, not the total owed.">
            <span class="flex cursor-default items-center gap-1.5 text-xs text-gray-500">
              <UiIcon name="repeat" class="h-3.5 w-3.5 text-gray-400" />
              Released to available
            </span>
          </UiTooltip>
          <span class="text-xs font-semibold tabular-nums text-gray-600">
            {{ formatCurrency(window.released_to_available_cents, currency) }}
          </span>
        </div>
      </template>
    </div>
  </UiCard>
</template>
